import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Percent, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const DEFAULT_BASE_COSTS = {
  per_document: 0.02,
  per_chatbot: 5.00,
  per_1000_messages: 1.50,
  per_100mb_storage: 2.00
};

export const PricingConfigPanel = () => {
  const [config, setConfig] = useState({
    profit_margin: 30,
    custom_plans_enabled: true,
    base_costs: DEFAULT_BASE_COSTS
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await api.get('/pricing-config/admin');
      setConfig({
        profit_margin: response.data.profit_margin || 30,
        custom_plans_enabled: response.data.custom_plans_enabled ?? true,
        base_costs: response.data.base_costs || DEFAULT_BASE_COSTS
      });
    } catch (error) {
      console.error('Error loading pricing config:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.patch('/pricing-config/admin', {
        profit_margin: config.profit_margin,
        custom_plans_enabled: config.custom_plans_enabled,
        base_costs: config.base_costs
      });
      toast.success('Pricing configuration saved');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const exampleConfig = { documents: 100, chatbots: 3, messages: 5000, storage_mb: 500 };
  const baseCost =
    (exampleConfig.documents * config.base_costs.per_document) +
    (exampleConfig.chatbots * config.base_costs.per_chatbot) +
    (exampleConfig.messages / 1000 * config.base_costs.per_1000_messages) +
    (exampleConfig.storage_mb / 100 * config.base_costs.per_100mb_storage);
  // PROFIT-MARGIN formula (matches backend iter126 fix):
  //   price = cost / (1 - margin/100)     [profit as % of final revenue]
  // NOT markup:
  //   price = cost × (1 + margin/100)     [profit as % of cost]
  // Capped at 95% to mirror server safety.
  const marginPct = Math.min(Math.max(config.profit_margin || 0, 0), 95);
  const marginMultiplier = 1 / (1 - marginPct / 100);
  const examplePrice = Math.ceil(baseCost * marginMultiplier * 100) / 100;
  const exampleProfit = Math.round((examplePrice - baseCost) * 100) / 100;
  const effectivePct = examplePrice > 0
    ? Math.round((exampleProfit / examplePrice) * 100 * 10) / 10
    : 0;

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm mt-6">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const baseCostFields = [
    { key: 'per_document', label: 'Per Document', step: '0.01' },
    { key: 'per_chatbot', label: 'Per AI Employee', step: '0.5' },
    { key: 'per_1000_messages', label: 'Per 1K Messages', step: '0.1' },
    { key: 'per_100mb_storage', label: 'Per 100MB Storage', step: '0.1' }
  ];

  return (
    <Card className="bg-white border-slate-200 shadow-sm mt-6">
      <CardHeader>
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <Percent className="w-5 h-5 text-emerald-600" />
          Custom Plan Pricing &amp; Profit Margin
        </CardTitle>
        <CardDescription className="text-slate-600">
          Control the profit percentage baked into every custom plan built via the pricing-page sliders. Users see the final marked-up price only — the base cost and margin stay internal. The Custom-vs-Fixed analysis below will flag when a fixed plan collides with the custom builder.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Settings */}
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-slate-700 font-medium">Enable Custom Plans</Label>
                <p className="text-sm text-slate-500">Allow users to build their own plans</p>
              </div>
              <Switch
                checked={config.custom_plans_enabled}
                onCheckedChange={(checked) => setConfig({ ...config, custom_plans_enabled: checked })}
              />
            </div>

            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-emerald-800 font-medium flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Your Profit Margin
                </Label>
              </div>
              <div className="relative">
                <Input
                  data-testid="custom-plan-profit-margin-input"
                  type="number"
                  min={0}
                  step={1}
                  value={config.profit_margin}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setConfig({ ...config, profit_margin: isNaN(v) ? 0 : Math.max(0, v) });
                  }}
                  className="pr-10 bg-white border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500 text-emerald-900 font-semibold text-lg"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 font-medium">%</span>
              </div>
              <p className="text-xs text-emerald-700 mt-2">
                Enter any positive markup (e.g., 30, 100, 250). No upper limit.
              </p>
            </div>

            <div>
              <Label className="text-slate-700 font-medium mb-3 block">Base Costs (per unit)</Label>
              <div className="grid grid-cols-2 gap-3">
                {baseCostFields.map(({ key, label, step }) => (
                  <div key={`base-cost-${key}`}>
                    <Label className="text-xs text-slate-500">{label}</Label>
                    <Input
                      type="number"
                      step={step}
                      value={config.base_costs[key]}
                      onChange={(e) => setConfig({
                        ...config,
                        base_costs: { ...config.base_costs, [key]: parseFloat(e.target.value) || 0 }
                      })}
                      className="mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={saveConfig} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
          </div>

          {/* Example Calculation */}
          <div>
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl">
              <h4 className="font-semibold text-slate-900 mb-4">Example Calculation</h4>
              <p className="text-sm text-slate-600 mb-4">
                For a user selecting: 100 docs, 3 AI Employees, 5K messages, 500MB storage
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Documents (100)</span>
                  <span className="text-slate-700">${(exampleConfig.documents * config.base_costs.per_document).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">AI Employees (3)</span>
                  <span className="text-slate-700">${(exampleConfig.chatbots * config.base_costs.per_chatbot).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Messages (5K)</span>
                  <span className="text-slate-700">${(exampleConfig.messages / 1000 * config.base_costs.per_1000_messages).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Storage (500MB)</span>
                  <span className="text-slate-700">${(exampleConfig.storage_mb / 100 * config.base_costs.per_100mb_storage).toFixed(2)}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-300 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Base Cost</span>
                  <span className="text-slate-700">${baseCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-emerald-700">
                  <span>Your Profit</span>
                  <span>+${exampleProfit.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-300">
                  <span className="text-slate-900">User Sees</span>
                  <span className="text-blue-600">${examplePrice.toFixed(2)}/mo</span>
                </div>
                <div className="pt-2 border-t border-slate-300 text-xs text-slate-500">
                  Effective margin: <span className="font-mono text-slate-800">{effectivePct}%</span> of revenue
                  &nbsp;·&nbsp;
                  <span className="font-mono">price = cost / (1 − margin/100)</span>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> Users configure their plan on the Pricing page using sliders.
                They see the final price (with your margin included). No admin approval needed. Your configured
                &quot;profit margin&quot; is the share of every dollar of revenue you keep — the effective margin
                above should match your input to the decimal.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingConfigPanel;
